<script setup lang="ts">
import { watch } from 'vue';
import { UiAside, UiButton, UiText, UiIcon, UiCheckbox } from '@shopexperts/shared';
import { useExpertsExport } from '@/app/composables/experts/useExpertsExport';
import type { ExpertExportField } from '@shopexperts/shared';

const asideOpen = defineModel<boolean>('open');

const emit = defineEmits<{
  (e: 'exported'): void;
}>();

const {
  availableFields,
  hasSelectedFields,
  isExporting,
  toggleField,
  selectAllFields,
  clearAllFields,
  isFieldSelected,
  startExport,
} = useExpertsExport({
  onSuccess: () => {
    emit('exported');
    // Close the aside after successful export
    asideOpen.value = false;
  },
});

// Select all fields by default when opening
watch(asideOpen, (isOpen) => {
  if (isOpen) {
    selectAllFields();
  }
}, { immediate: true });

const onCancel = () => {
  asideOpen.value = false;
};

const onExport = async () => {
  await startExport();
};
</script>

<template>
  <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="!isExporting">
    <div class="px-4 py-6 md:p-8 flex flex-col gap-4 h-full overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <UiText as="h3" variant="h3">
          <span class="font-normal">Export </span>
          <span class="font-besley italic">Experts</span>
        </UiText>
        <UiButton 
          variant="ghost" 
          shape="square" 
          :disabled="isExporting"
          @click="onCancel"
        >
          <template #before_label>
            <UiIcon name="close" class="w-6 h-6" />
          </template>
        </UiButton>
      </div>

      <!-- Description -->
      <UiText 
        as="p" 
        variant="p" 
        class="text-se-gray-600"
        text="Select the fields you want to include in your CSV export. The export will include all experts in the system."
      />

      <!-- Select All / Clear All -->
      <div class="flex items-center gap-4 pb-2 border-b border-se-gray-200">
        <button 
          type="button"
          class="text-sm text-se-primary hover:underline disabled:opacity-50"
          :disabled="isExporting"
          @click="selectAllFields"
        >
          Select All
        </button>
        <button 
          type="button"
          class="text-sm text-se-gray-500 hover:underline disabled:opacity-50"
          :disabled="isExporting"
          @click="clearAllFields"
        >
          Clear All
        </button>
      </div>

      <!-- Field Selection -->
      <div class="flex flex-col gap-3 flex-1">
        <div
          v-for="field in availableFields"
          :key="field.value"
          class="flex items-center gap-3"
        >
          <UiCheckbox
            :label="field.label"
            :model-value="isFieldSelected(field.value)"
            :disabled="isExporting"
            @update:model-value="toggleField(field.value as ExpertExportField)"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div 
        v-if="isExporting" 
        class="flex items-center gap-3 p-4 bg-se-gray-50 rounded-lg"
      >
        <div class="animate-spin">
          <UiIcon name="repeat" class="w-5 h-5 text-se-primary" />
        </div>
        <UiText 
          as="p" 
          variant="p-small" 
          class="text-se-gray-600"
          text="Generating export... This may take a moment for large datasets."
        />
      </div>

      <!-- Action Buttons -->
      <div class="flex items-center gap-3 pt-4 border-t border-se-gray-200">
        <UiButton 
          variant="primary" 
          :label="isExporting ? 'Exporting...' : 'Export to CSV'"
          :disabled="!hasSelectedFields || isExporting"
          @click="onExport"
        >
          <template v-if="!isExporting" #before_label>
            <UiIcon name="upload" class="w-5 h-5" />
          </template>
        </UiButton>
        <UiButton 
          variant="outline" 
          label="Cancel"
          :disabled="isExporting"
          @click="onCancel"
        />
      </div>
    </div>
  </UiAside>
</template>
